import React, { useState, useEffect, useCallback, useMemo } from 'react';
import LocationForm from './LocationForm';
import {
    MapPin,
    PlusCircle,
    Edit,
    Trash,
    AlertCircle,
    CheckCircle,
    Users,
    ShieldCheck,
    ArrowRightLeft,
    CheckCircle2,
    XCircle
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './common/DataTable';
import InfoModal from './common/InfoModal';

export default function LocationManagement() {
    const [locationList, setLocationList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    // Silme & Aktarım Modal State'leri
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [locationToDelete, setLocationToDelete] = useState(null);
    const [deleteResolutionMode, setDeleteResolutionMode] = useState('transfer'); // 'transfer' | 'clear'
    const [transferTargetId, setTransferTargetId] = useState('');
    const [deleteModalError, setDeleteModalError] = useState(null);
    const [forceTransferRequired, setForceTransferRequired] = useState(false);

    // Sadece Personel/Yetki Aktarımı (Lokasyonu Silmeden) Modal State'i
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [locationToTransfer, setLocationToTransfer] = useState(null);
    const [transferOnlyTargetId, setTransferOnlyTargetId] = useState('');
    const [transferDataScope, setTransferDataScope] = useState(true);
    const [transferModalError, setTransferModalError] = useState(null);

    const { isAuthenticated, permissions = [], activeSubCompanyCode } = useAuth();

    const canView = useMemo(
        () => permissions.includes('locations:view_list') || permissions.includes('departments:view_list') || permissions.includes('personnel:view_list'),
        [permissions]
    );
    const canCreate = useMemo(
        () => permissions.includes('locations:create') || permissions.includes('departments:create'),
        [permissions]
    );
    const canUpdate = useMemo(
        () => permissions.includes('locations:update') || permissions.includes('departments:update'),
        [permissions]
    );
    const canDelete = useMemo(
        () => permissions.includes('locations:delete') || permissions.includes('departments:delete'),
        [permissions]
    );

    const fetchLocations = useCallback(async () => {
        if (!canView) {
            setError("Lokasyonları listeleme yetkiniz bulunmamaktadır.");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await apiClient('Location');
            setLocationList(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Lokasyonlar çekilirken hata oluştu:", err);
            setError(err.message || "Lokasyonları getirirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [canView, activeSubCompanyCode]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchLocations();
        }
    }, [isAuthenticated, fetchLocations]);

    const summaryStats = useMemo(() => {
        const total = locationList.length;
        const activeLocs = locationList.filter(l => l.isActive).length;
        const passiveLocs = total - activeLocs;
        const totalActiveEmployees = locationList.reduce((acc, l) => acc + (l.activeEmployeeCount || 0), 0);
        const scopedLocs = locationList.filter(l => (l.authorizedUserCount || 0) > 0).length;
        return { total, activeLocs, passiveLocs, totalActiveEmployees, scopedLocs };
    }, [locationList]);

    const handleAddClick = () => {
        setSelectedLocation(null);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleEditClick = (location) => {
        setSelectedLocation(location);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleTransferClick = (location) => {
        setLocationToTransfer(location);
        setTransferOnlyTargetId('');
        setTransferDataScope(true);
        setTransferModalError(null);
        setIsTransferModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleDeleteRequest = (location) => {
        setLocationToDelete(location);
        setTransferTargetId('');
        setDeleteResolutionMode('transfer');
        setDeleteModalError(null);
        setForceTransferRequired((location.totalEmployeeCount || 0) > 0);
        setIsConfirmModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleDeleteConfirm = async () => {
        if (!locationToDelete) return;

        const needsResolution = forceTransferRequired || (locationToDelete.totalEmployeeCount || 0) > 0;
        if (needsResolution && deleteResolutionMode === 'transfer' && !transferTargetId) {
            setDeleteModalError("Bağlı personellerin aktarılacağı hedef lokasyonu seçin veya 'Lokasyon Atamasını Kaldır' seçeneğini işaretleyin.");
            return;
        }

        setActionLoading(true);
        setDeleteModalError(null);
        try {
            let endpoint = `Location/${locationToDelete.id}`;
            if (needsResolution) {
                if (deleteResolutionMode === 'transfer' && transferTargetId) {
                    endpoint += `?transferToLocationId=${transferTargetId}`;
                } else if (deleteResolutionMode === 'clear') {
                    endpoint += `?clearEmployeeLocation=true`;
                }
            }

            await apiClient(endpoint, { method: 'DELETE' });
            setIsConfirmModalOpen(false);
            setLocationToDelete(null);
            setTransferTargetId('');
            setForceTransferRequired(false);
            setMessage(
                needsResolution && deleteResolutionMode === 'transfer' && transferTargetId
                    ? `"${locationToDelete.name}" lokasyonundaki personeller ve veri kapsamı yetkileri hedef lokasyona aktarıldı ve lokasyon silindi.`
                    : `"${locationToDelete.name}" lokasyonu başarıyla silindi.`
            );
            await fetchLocations();
        } catch (err) {
            const msg = err.message || "Lokasyon silinirken bir hata oluştu.";
            if (msg.includes("personel") || msg.includes("arşivlenmiş") || msg.includes("aktar")) {
                setForceTransferRequired(true);
            }
            setDeleteModalError(msg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleTransferConfirm = async () => {
        if (!locationToTransfer) return;
        if (!transferOnlyTargetId) {
            setTransferModalError("Lütfen personellerin aktarılacağı hedef lokasyonu seçin.");
            return;
        }

        setActionLoading(true);
        setTransferModalError(null);
        try {
            const res = await apiClient(`Location/${locationToTransfer.id}/transfer`, {
                method: 'POST',
                body: {
                    targetLocationId: Number(transferOnlyTargetId),
                    transferDataScopeAccess: transferDataScope
                }
            });
            setIsTransferModalOpen(false);
            setLocationToTransfer(null);
            setTransferOnlyTargetId('');
            setMessage(res?.message || "Lokasyon personelleri başarıyla aktarıldı.");
            await fetchLocations();
        } catch (err) {
            setTransferModalError(err.message || "Personel aktarımı sırasında bir hata oluştu.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleFormSubmit = async (locationData) => {
        setActionLoading(true);
        setError(null);
        try {
            const method = locationData.id ? 'PUT' : 'POST';
            const endpoint = locationData.id ? `Location/${locationData.id}` : 'Location';
            await apiClient(endpoint, {
                method,
                body: locationData,
            });
            setMessage(`"${locationData.name}" lokasyonu başarıyla ${locationData.id ? 'güncellendi' : 'eklendi'}!`);
            setIsModalOpen(false);
            setSelectedLocation(null);
            await fetchLocations();
        } catch (err) {
            throw err;
        } finally {
            setActionLoading(false);
        }
    };

    const handleModalClose = () => {
        if (actionLoading) return;
        setIsModalOpen(false);
        setIsConfirmModalOpen(false);
        setIsTransferModalOpen(false);
        setSelectedLocation(null);
        setLocationToDelete(null);
        setLocationToTransfer(null);
        setTransferTargetId('');
        setTransferOnlyTargetId('');
        setDeleteModalError(null);
        setTransferModalError(null);
        setForceTransferRequired(false);
    };

    const availableTargetLocationsForDelete = useMemo(() => {
        if (!locationToDelete) return [];
        return locationList.filter(l => l.id !== locationToDelete.id && l.isActive);
    }, [locationList, locationToDelete]);

    const availableTargetLocationsForTransfer = useMemo(() => {
        if (!locationToTransfer) return [];
        return locationList.filter(l => l.id !== locationToTransfer.id && l.isActive);
    }, [locationList, locationToTransfer]);

    const locationColumns = useMemo(() => {
        const columns = [
            {
                header: 'Lokasyon / Şube Adı',
                accessor: 'name',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            row.isActive
                                ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                        }`}>
                            <MapPin className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-semibold text-gray-900 dark:text-gray-100">{row.name}</div>
                            <div className="text-[11px] text-gray-400 dark:text-gray-500">Kod: #{row.id}</div>
                        </div>
                    </div>
                )
            },
            {
                header: 'Açıklama',
                accessor: 'description',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <span className="text-gray-600 dark:text-gray-300 text-xs whitespace-normal line-clamp-2 max-w-md">
                        {row.description || '-'}
                    </span>
                )
            },
            {
                header: 'Personel Sayısı',
                accessor: 'activeEmployeeCount',
                sortable: true,
                searchable: false,
                getFilterValue: (row) => String(row.activeEmployeeCount ?? 0),
                render: (row) => {
                    const active = row.activeEmployeeCount || 0;
                    const inactive = row.inactiveEmployeeCount || 0;
                    return (
                        <div className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                active > 0
                                    ? 'bg-blue-50 text-blue-700 border border-blue-200/80 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                                <Users className="w-3.5 h-3.5" />
                                {active.toLocaleString('tr-TR')} Aktif
                            </span>
                            {inactive > 0 && (
                                <span
                                    className="text-[11px] text-gray-400 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/70 px-2 py-0.5 rounded-full"
                                    title="İşten ayrılmış / pasif personel sayısı"
                                >
                                    +{inactive} Pasif
                                </span>
                            )}
                        </div>
                    );
                }
            },
            {
                header: 'Veri Kapsamı (DataScope)',
                accessor: 'authorizedUserCount',
                sortable: true,
                searchable: false,
                getFilterValue: (row) => String(row.authorizedUserCount ?? 0),
                render: (row) => {
                    const authCount = row.authorizedUserCount || 0;
                    return authCount > 0 ? (
                        <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200/80 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800/60"
                            title="Bu lokasyonu görmeye yetkili (DataScope atanmış) kullanıcı sayısı"
                        >
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {authCount} Yetkili Kullanıcı
                        </span>
                    ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Genel (Tüm Veri)</span>
                    );
                }
            },
            {
                header: 'Durum',
                accessor: 'isActive',
                sortable: true,
                searchable: false,
                getFilterValue: (row) => row.isActive ? 'Aktif' : 'Pasif',
                render: (row) => row.isActive ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aktif
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
                        <XCircle className="w-3.5 h-3.5" /> Pasif
                    </span>
                )
            },
        ];

        if (canUpdate || canDelete) {
            columns.push({
                header: 'Aksiyonlar',
                accessor: 'actions',
                filterable: false,
                render: (row) => (
                    <div className="flex items-center space-x-1.5">
                        {canUpdate && (
                            <button
                                onClick={() => handleEditClick(row)}
                                className="p-1.5 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors"
                                title="Lokasyonu Düzenle"
                            >
                                <Edit className="w-4 h-4" />
                            </button>
                        )}
                        {canUpdate && ((row.totalEmployeeCount || 0) > 0 || (row.authorizedUserCount || 0) > 0) && (
                            <button
                                onClick={() => handleTransferClick(row)}
                                className="p-1.5 rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-gray-700 transition-colors"
                                title="Personelleri ve Veri Kapsamını Başka Lokasyona Aktar"
                            >
                                <ArrowRightLeft className="w-4 h-4" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => handleDeleteRequest(row)}
                                className="p-1.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-gray-700 transition-colors"
                                title="Lokasyonu Sil"
                            >
                                <Trash className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ),
            });
        }
        return columns;
    }, [canUpdate, canDelete]);

    if (!canView && !loading) {
        return (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-xl flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 shrink-0" />
                Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.
            </div>
        );
    }

    const deleteNeedsResolution = locationToDelete && (forceTransferRequired || (locationToDelete.totalEmployeeCount || 0) > 0);

    return (
        <div className="p-1 space-y-4">
            {/* Üst Özet Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Toplam Lokasyon</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {summaryStats.total.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{summaryStats.activeLocs} Aktif</span>
                            {summaryStats.passiveLocs > 0 && ` • ${summaryStats.passiveLocs} Pasif`}
                        </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                        <MapPin className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Lokasyonlardaki Aktif Personel</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {summaryStats.totalActiveEmployees.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            Lokasyon atanmış aktif çalışanlar
                        </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                        <Users className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Veri Kapsamı (DataScope) Filtreli Lokasyon</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {summaryStats.scopedLocs.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            Lokasyon bazlı yetkilendirilmiş tesisler
                        </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6" />
                    </div>
                </div>
            </div>

            {message && (
                <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 p-3.5 rounded-xl flex items-center justify-between">
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2.5 text-emerald-500 shrink-0" />
                        <span className="text-sm font-medium">{message}</span>
                    </div>
                    <button onClick={() => setMessage(null)} className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline ml-4">Kapat</button>
                </div>
            )}

            {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 p-3.5 rounded-xl flex items-center justify-between">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2.5 text-red-500 shrink-0" />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                    <button onClick={() => setError(null)} className="text-xs text-red-600 dark:text-red-400 hover:underline ml-4">Kapat</button>
                </div>
            )}

            <DataTable
                data={locationList}
                columns={locationColumns}
                itemsPerPage={10}
                pageSizeOptions={[10, 20, 50, 100]}
                showSearch={true}
                searchPlaceholder="Lokasyon adı veya açıklamada ara..."
                showPagination={true}
                emptyMessage="Henüz hiçbir lokasyon eklenmemiş."
                isLoading={loading}
                errorMessage={null}
                actionsHeader={
                    canCreate && (
                        <button
                            onClick={handleAddClick}
                            className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
                        >
                            <PlusCircle className="w-4 h-4 mr-2" />
                            Yeni Lokasyon Ekle
                        </button>
                    )
                }
            />

            {/* Lokasyon Ekle/Düzenle Modalı */}
            <InfoModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                title={selectedLocation ? `Lokasyon Düzenle: ${selectedLocation.name}` : "Yeni Lokasyon Ekle"}
                showFooter={false}
            >
                <LocationForm
                    location={selectedLocation}
                    onSave={handleFormSubmit}
                    onCancel={handleModalClose}
                    isNew={!selectedLocation}
                    submitting={actionLoading}
                />
            </InfoModal>

            {/* Lokasyon Personellerini Başka Lokasyona Aktar Modalı (Silmeden) */}
            <InfoModal
                isOpen={isTransferModalOpen}
                onClose={handleModalClose}
                title="Lokasyon Personellerini ve Yetkilerini Aktar"
                type="warning"
                onConfirm={handleTransferConfirm}
                isLoading={actionLoading}
                confirmText="Aktarımı Başlat"
                cancelText="Vazgeç"
                confirmDisabled={!transferOnlyTargetId}
            >
                {locationToTransfer && (
                    <div className="space-y-4">
                        {transferModalError && (
                            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 p-3 rounded-xl text-xs">
                                {transferModalError}
                            </div>
                        )}
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>{locationToTransfer.name}</strong> lokasyonundaki{' '}
                            <strong>{locationToTransfer.totalEmployeeCount || 0} personeli</strong> başka bir lokasyona topluca aktarmak için hedef lokasyonu seçin:
                        </p>

                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                Hedef Lokasyon Seçin <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={transferOnlyTargetId}
                                onChange={(e) => setTransferOnlyTargetId(e.target.value)}
                                className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">-- Aktarılacak Lokasyonu Seçin --</option>
                                {availableTargetLocationsForTransfer.map(l => (
                                    <option key={l.id} value={l.id}>
                                        {l.name} ({l.activeEmployeeCount || 0} Aktif Personel)
                                    </option>
                                ))}
                            </select>
                        </div>

                        <label className="flex items-start gap-2.5 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={transferDataScope}
                                onChange={(e) => setTransferDataScope(e.target.checked)}
                                className="mt-0.5 h-4 w-4 text-indigo-600 rounded border-gray-300"
                            />
                            <span className="text-xs text-gray-700 dark:text-gray-300">
                                <strong>Veri Görme Kapsamı (DataScope) yetkilerini de kopyala:</strong> Bu lokasyonu görmeye yetkili yöneticiler hedef lokasyonu da görebilsin.
                            </span>
                        </label>
                    </div>
                )}
            </InfoModal>

            {/* Akıllı Silme & Aktararak Silme Onay Modalı */}
            <InfoModal
                isOpen={isConfirmModalOpen}
                onClose={handleModalClose}
                title="Lokasyon Silme Onayı"
                type="confirm"
                onConfirm={handleDeleteConfirm}
                isLoading={actionLoading}
                confirmText={
                    deleteNeedsResolution
                        ? (deleteResolutionMode === 'transfer' ? "Personelleri Aktar ve Lokasyonu Sil" : "Atamaları Kaldır ve Lokasyonu Sil")
                        : "Evet, Lokasyonu Sil"
                }
                cancelText="Vazgeç"
                confirmDisabled={deleteNeedsResolution && deleteResolutionMode === 'transfer' && !transferTargetId}
            >
                {locationToDelete && (
                    <div className="space-y-4">
                        {deleteModalError && (
                            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 p-3 rounded-xl text-xs">
                                {deleteModalError}
                            </div>
                        )}

                        {deleteNeedsResolution ? (
                            <>
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-200 p-3.5 rounded-xl text-xs space-y-1.5">
                                    <p className="font-semibold text-sm">
                                        "{locationToDelete.name}" lokasyonuna bağlı kayıtlar bulundu!
                                    </p>
                                    <p>
                                        Bu lokasyonda <strong>{locationToDelete.activeEmployeeCount || 0} aktif</strong>
                                        {(locationToDelete.inactiveEmployeeCount || 0) > 0 && `, ${locationToDelete.inactiveEmployeeCount} pasif`} personel
                                        {(locationToDelete.authorizedUserCount || 0) > 0 && ` ve ${locationToDelete.authorizedUserCount} yöneticinin veri kapsamı (DataScope) yetkisi`} bulunmaktadır.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label className="flex items-start gap-2.5 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="deleteLocMode"
                                            checked={deleteResolutionMode === 'transfer'}
                                            onChange={() => setDeleteResolutionMode('transfer')}
                                            className="mt-0.5 text-indigo-600"
                                        />
                                        <div className="text-xs">
                                            <div className="font-semibold text-gray-800 dark:text-gray-100">Başka Bir Lokasyona Aktararak Sil (Önerilen)</div>
                                            <div className="text-gray-500 dark:text-gray-400">Bağlı tüm personeller ve veri kapsamı yetkileri seçtiğiniz yeni lokasyona taşınır.</div>
                                        </div>
                                    </label>

                                    {deleteResolutionMode === 'transfer' && (
                                        <div className="pl-6">
                                            <select
                                                value={transferTargetId}
                                                onChange={(e) => setTransferTargetId(e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            >
                                                <option value="">-- Hedef Lokasyon Seçin --</option>
                                                {availableTargetLocationsForDelete.map(l => (
                                                    <option key={l.id} value={l.id}>
                                                        {l.name} ({l.activeEmployeeCount || 0} Aktif Personel)
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <label className="flex items-start gap-2.5 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="deleteLocMode"
                                            checked={deleteResolutionMode === 'clear'}
                                            onChange={() => setDeleteResolutionMode('clear')}
                                            className="mt-0.5 text-red-600"
                                        />
                                        <div className="text-xs">
                                            <div className="font-semibold text-gray-800 dark:text-gray-100">Personellerin Lokasyon Atamasını Kaldırarak Sil</div>
                                            <div className="text-gray-500 dark:text-gray-400">Personeller silinmez; yalnızca lokasyon bilgisi "Belirtilmedi" olarak güncellenir.</div>
                                        </div>
                                    </label>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    <strong>"{locationToDelete.name}"</strong> lokasyonunu kalıcı olarak silmek istediğinizden emin misiniz?
                                </p>
                                {(locationToDelete.authorizedUserCount || 0) > 0 && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2.5 rounded-lg border border-amber-200 dark:border-amber-800/50">
                                        Not: Bu lokasyon üzerinde tanımlı <strong>{locationToDelete.authorizedUserCount} kullanıcının</strong> veri görme kapsamı (DataScope) eşleşmesi de kaldırılacaktır.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </InfoModal>
        </div>
    );
}
